import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, Users, Bed, Check, Phone } from "lucide-react";
import { BOOKING_URL, NAP } from "../lib/config";

export default function RoomDialog({ room, open, onOpenChange }) {
  const [active, setActive] = useState(0);
  if (!room) return null;
  const photos = room.photos && room.photos.length ? room.photos : [room.hero_photo];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 bg-[#FDFBF7] border-[#E5DFD3] max-h-[92vh] overflow-y-auto"
        data-testid={`room-dialog-${room.slug}`}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{room.name}</DialogTitle>
          <DialogDescription>{room.tagline}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-[#1A1A1A]">
            <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full">
              <img
                src={photos[active]}
                alt={`${room.name} photo ${active + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            {photos.length > 1 && (
              <div className="p-3 flex gap-2 overflow-x-auto bg-[#1A1A1A]">
                {photos.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => setActive(i)}
                    data-testid={`room-thumb-${room.slug}-${i}`}
                    className={`flex-shrink-0 w-20 h-16 overflow-hidden rounded-sm border-2 ${
                      i === active ? "border-[#E85D26]" : "border-white/20"
                    }`}
                  >
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 sm:p-8 lg:p-10 flex flex-col">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="wb-eyebrow">
                  {room.is_flagship ? "Flagship suite" : "Guest room"}
                </div>
                <h3 className="wb-h3 mt-2">{room.name}</h3>
                <p
                  className="text-[#B94A15] font-bold mt-1"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {room.tagline}
                </p>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="p-2 text-[#4A4641] hover:text-[#B94A15]"
                aria-label="Close"
                data-testid={`room-dialog-close-${room.slug}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm text-[#4A4641] font-medium">
              <span className="flex items-center gap-1.5">
                <Bed className="h-4 w-4 text-[#B94A15]" /> {room.bed_config}
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-[#B94A15]" /> Up to {room.max_guests} guests
              </span>
              {room.size_sqft && (
                <span>~{room.size_sqft} sq ft</span>
              )}
            </div>

            <p className="mt-6 wb-body text-base">{room.description}</p>

            <div className="mt-6">
              <div className="wb-eyebrow mb-3">Room features</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {room.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4A4641]">
                    <Check className="h-4 w-4 text-[#B94A15] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-[#E5DFD3] flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs text-[#4A4641] font-medium">Starting at</div>
                <div
                  className="text-3xl font-black text-[#B94A15]"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  ${room.price_from.toFixed(2)}
                  <span className="text-sm text-[#4A4641] font-medium ml-1">/night</span>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={`tel:${NAP.phoneTel}`}
                  className="wb-btn-outline text-sm"
                  data-testid={`room-dialog-call-${room.slug}`}
                >
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wb-btn-primary"
                  data-testid={`room-dialog-book-${room.slug}`}
                >
                  Book Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
